import type { LayoutDocument, MediaFit, MediaItem } from './types'

/**
 * How a hall board steps through a playlist. Pure, so the rules are tested in Node
 * rather than discovered on a wall.
 *
 * The board runs unattended and may lose the server for hours. It keeps playing
 * what it has; an item that will not load is skipped rather than shown as a broken
 * picture; and a list where nothing loads rests for half a minute and tries again,
 * instead of spinning through failures as fast as the browser can report them.
 */

export const DEFAULT_PICTURE_SECONDS = 10
/** A film normally moves on at its ended event. This is for one that stalls. */
export const MAX_FILM_MS = 600_000
export const RETRY_AFTER_ALL_FAILED_MS = 30_000
/** The soonest a board asks again because the time changed. See reaskDelay. */
export const REASK_FLOOR_MS = 5_000

/** How long an item stays before the board moves on by the clock. */
export function itemMs(item: MediaItem): number {
  if (item.seconds && item.seconds > 0) return item.seconds * 1000
  return item.kind === 'video' ? MAX_FILM_MS : DEFAULT_PICTURE_SECONDS * 1000
}

/** A film's volume while a call is chimed or read out: the call is what the hall must hear. */
export const DUCKED_VOLUME = 0.2

/** What startFilm needs of a <video>. */
export interface Startable {
  muted: boolean
  play(): Promise<void>
}

/**
 * Starts a film, with its sound when it has one. A browser refuses sound until the
 * page has been pressed; the film then plays muted rather than standing still.
 */
export async function startFilm(video: Startable, sound: boolean): Promise<'sound' | 'muted' | 'refused'> {
  video.muted = !sound
  try {
    await video.play()
    return sound ? 'sound' : 'muted'
  } catch (err) {
    if (!sound || (err as { name?: string } | null)?.name !== 'NotAllowedError') return 'refused'
  }
  video.muted = true
  try {
    await video.play()
    return 'muted'
  } catch {
    return 'refused'
  }
}

/** The next item that has not failed, going round; null when none is left. */
export function nextIndex(from: number, count: number, failed: ReadonlySet<number>): number | null {
  for (let step = 1; step <= count; step++) {
    const i = (from + step) % count
    if (!failed.has(i)) return i
  }
  return null
}

/** What a list is, for telling a new list from the one playing. */
export function playlistKey(items: readonly MediaItem[]): string {
  return items.map((i) => `${i.kind}:${i.assetId}:${i.seconds ?? 0}:${i.fit ?? ''}`).join('|')
}

/**
 * What a board has drawn: the version, and what each playlist on it plays and its ticker.
 *
 * Deliberately not the server's `playing` key. That names the time a playlist is
 * in, and it changes when the list does not: a late-night time written as two
 * (22:00–24:00 and 00:00–02:00) moves from "ads:0" to "ads:1" at midnight with the
 * same items. Comparing the items means a film halfway through is not handed a new
 * document, and so is not cut back to its start, for a change nobody can see.
 */
export function drawnKey(version: number, doc: LayoutDocument): string {
  const lists = doc.elements
    .filter((el) => el.kind === 'media')
    .map((el) => {
      const ticker = el.media?.ticker ?? []
      return `${el.id}=${playlistKey(el.media?.items ?? [])}${ticker.length > 0 ? `#${JSON.stringify(ticker)}` : ''}`
    })
  return `${version}|${lists.join(',')}`
}

/** How an item fills its area on screen: fill crops, fit letterboxes, centre keeps its own size. */
export function objectFitOf(fit?: MediaFit): 'cover' | 'contain' | 'none' {
  if (fit === 'fill') return 'cover'
  if (fit === 'centre') return 'none'
  return 'contain'
}

/**
 * Where a player is in its list.
 *
 * `round` counts every move. It is the token that says which showing a signal
 * belongs to — a film's ended event and its clock can both fire for one showing,
 * and the second must not skip an item nobody has seen — and it keys the film, so
 * a film playing again after itself is a new <video> that starts from the
 * beginning. `key` is the list this place belongs to (playlistKey).
 */
export interface Place {
  key: string
  index: number
  round: number
  resting: boolean
  failed: ReadonlySet<number>
  /** How many times the whole list has been tried again after resting. It keys a
   *  picture, so a picture that failed is fetched afresh rather than left as the
   *  same broken <img> the browser will not ask for twice. */
  attempt: number
}

export function startOf(key: string): Place {
  return { key, index: 0, round: 0, resting: false, failed: new Set(), attempt: 0 }
}

/** After resting, the whole list again from the top, with nothing held against it. */
export function retry(p: Place): Place {
  if (!p.resting) return p
  return { ...startOf(p.key), round: p.round + 1, attempt: p.attempt + 1 }
}

/**
 * The showing numbered `round` is over. Anything else is a late signal, ignored.
 *
 * `failed` holds only for one pass round the list. Going past the end forgets it,
 * so an item that failed because the network blinked gets another chance next time
 * round, instead of staying skipped until somebody republishes the design. A pass in
 * which every item failed is the one case that rests: going round again at once
 * would spin through failures as fast as the browser reports them.
 */
export function advance(p: Place, count: number, round: number): Place {
  if (p.round !== round || p.resting) return p
  for (let i = p.index + 1; i < count; i++) {
    if (!p.failed.has(i)) return { ...p, index: i, round: p.round + 1 }
  }
  if (count === 0 || p.failed.size >= count) return { ...p, resting: true }
  return { ...p, index: 0, round: p.round + 1, failed: new Set() }
}

/** The item at `index`, showing as `round`, would not load. */
export function failed(p: Place, count: number, index: number, round: number): Place {
  if (p.round !== round || p.index !== index || p.resting) return p
  return advance({ ...p, failed: new Set(p.failed).add(index) }, count, round)
}

/**
 * How long to wait before asking the server what plays now, or null to leave it to
 * the half-minute poll.
 *
 * A second after the change, so the server's clock is past it too, plus jitter so
 * that twenty boards in a building do not all ask in the same millisecond.
 *
 * Never sooner than a few seconds, and never twice for the same change
 * (`alreadyAsked` is the changesAt the last timed ask was for). A board's clock is
 * whatever the panel's firmware says: one running ahead of the server sees every
 * change as already past, asks, is told the same changesAt because the server has
 * not reached it yet, and without these two rules would ask again at once, for as
 * long as the clocks disagree. With them it asks once, and the poll does the rest.
 *
 * A change more than a day off is left to the poll: every poll works this out
 * afresh, so the timer is set once the change comes within the day, and no timer
 * is ever long enough for a browser to fire it at once instead (anything over
 * about 24.8 days does).
 */
export function reaskDelay(
  changesAt: string | undefined,
  now: number,
  jitterMs: number,
  alreadyAsked?: string,
): number | null {
  if (!changesAt || changesAt === alreadyAsked) return null
  const at = Date.parse(changesAt)
  if (Number.isNaN(at)) return null
  const until = at - now
  if (until > 24 * 3600_000) return null
  return Math.max(REASK_FLOOR_MS, until + 1000) + jitterMs
}

/** How often a playing film is looked at, and how long it may stand still. */
export const STALL_CHECK_MS = 5_000
export const STALL_LIMIT_MS = 20_000

/** How long a film that has not started yet is left to buffer before the watchdog
 *  looks at it anyway. */
export const FILM_START_GRACE_MS = 60_000

/**
 * Whether the stall watchdog looks at a film yet.
 *
 * Only once the film has played, or once it has had a minute to start. A slow
 * first buffer — a large film over a branch's thin line — stands at zero for
 * longer than the stall limit, and a watchdog armed from the mount would take it
 * for a stall, make it again from a fresh request, and never let it start. A film
 * that has not started after a minute is watched as any other, so a refused start
 * still moves the list on.
 */
export function filmWatchArmed(played: boolean, mountedAt: number, now: number): boolean {
  return played || now - mountedAt >= FILM_START_GRACE_MS
}

export interface FilmWatch {
  /** The film's time at the last look. */
  time: number
  /** When that time last changed. */
  since: number
  stalled: boolean
}

/**
 * Whether a film that should be playing has stopped moving.
 *
 * A film can stop without an error: the network drops and the browser waits for
 * data that never comes, or the browser refuses to start it and the refusal is
 * swallowed. Neither fires an event that moves the list on, and for a lone looping
 * film nothing else does either, so the board would show one frame until the next
 * republish. Any change in the film's time counts as progress, including a loop
 * going back to the start. `last` is the previous look; undefined for the first.
 */
export function watchFilm(last: FilmWatch | undefined, time: number, now: number): FilmWatch {
  if (!last || time !== last.time) return { time, since: now, stalled: false }
  return { ...last, stalled: now - last.since >= STALL_LIMIT_MS }
}

/**
 * Which answer a screen takes when its requests overlap.
 *
 * The poll, the timed ask and a slow network can leave two requests in flight, and
 * nothing makes them come back in order. An answer is taken unless a newer one has
 * already been taken, so a slow old answer cannot put back a list the board has
 * already moved past; an older one still counts while the newer is out.
 */
export function answerOrder() {
  let asked = 0
  let taken = 0
  return {
    ask: () => ++asked,
    take: (n: number) => {
      if (n <= taken) return false
      taken = n
      return true
    },
  }
}
